import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'

import { selectUserData } from '../../../reducers/userDataReducer'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { INTEGRATION_TYPES, getPmsIntegrationType } from '../OrganisationPlaces/helpers'

import PayNowView from './PayNowView'
import PayRentViewYardi from './PayRentViewYardi'
import { getChargePayable, groupPaymentHistory, normalizeYardiCharges } from './helpers'

const Payments = ({ currentUser }) => {
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['organisation'])
  const [selectedTransactions, selectTransaction] = useState([])
  const [yardiTransactions, setYardiTransactions] = useState([])
  const [paymentHistory, setPaymentHistory] = useState([])
  const [paymentHistoryError, setPaymentHistoryError] = useState(null)
  const [blockPayment, setBlockPayment] = useState(false)
  const [blockPaymentMessage, setBlockPaymentMessage] = useState('')

  const [isPayNowMode, setPayNowMode] = useState(false)
  const [editPriceTransaction, setEditPriceTransaction] = useState(null)
  const [customTotal, setCustomTotal] = useState(null)

  const [totalPay, setTotalPay] = useState(null)
  const [originalPay, setOriginalPay] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isLoading, setLoading] = useState(false)

  const [autopaySettings, setAutopaySettings] = useState(null)

  const [paymentSettings, setPaymentSettings] = useState({
    allowAutopay: false,
    allowChargesSelection: false,
    allowPartialPayment: false,
    stripeEnabled: false,
  })

  // GET/POST /client/transactions is PMS-agnostic (Yardi + RentManager) — both PMSs reuse this
  // path, so "isYardi" here really means "has a live-charges PMS integration".
  const pmsType = currentUser && getPmsIntegrationType(currentUser.buildingId)
  const isYardi = pmsType === INTEGRATION_TYPES.YARDI || pmsType === INTEGRATION_TYPES.RENT_MANAGER
  // Pre-PayRent history can only be pulled from RentManager; every other PMS (e.g. Yardi) shows a
  // "connect your property" placeholder instead of the generic empty state.
  const isPmsHistoryUnavailable = pmsType !== INTEGRATION_TYPES.RENT_MANAGER

  const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

  const getClientYardiTransactions = async () => {
    setLoading(true)
    try {
      const { data } = await API.getYardiTransactions()
      // /client/transactions is now PMS-agnostic (Yardi + RentManager) — Yardi still replies with
      // the bare charge-group array, RentManager wraps it as { charges }. Normalize here so every
      // consumer (PayRentViewYardi, PayNowView) keeps working off a flat array.
      const charges = Array.isArray(data) ? data : data?.charges || []
      setYardiTransactions(normalizeYardiCharges(charges))
      setBlockPayment(!!data?.blockPayment)
      setBlockPaymentMessage(data?.blockPaymentMessage || '')
    } catch {
      setYardiTransactions([])
      setBlockPayment(false)
      setBlockPaymentMessage('')
      displaySnackbar(t('organisation:payments.errors.fetchYardiTransactionsFailed'), true)
    } finally {
      setLoading(false)
    }
  }

  const getClientPaymentHistory = async () => {
    try {
      const { data } = await API.getPaymentHistory()
      setPaymentHistory(groupPaymentHistory(data?.data))
      setPaymentHistoryError(null)
    } catch {
      setPaymentHistory([])
      setPaymentHistoryError(t('organisation:payments.errors.loadHistoryFailed'))
    }
  }

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await API.getUserPaymentSettings()
      setPaymentSettings(data)
    } catch {
      setPaymentSettings({
        allowAutopay: false,
        allowChargesSelection: false,
        allowPartialPayment: false,
        stripeEnabled: false,
      })
      displaySnackbar(t('organisation:payments.errors.fetchPaymentSettingsFailed'), true)
    }
  }

  const fetchAutopaySettings = async () => {
    try {
      const { data } = await API.getAutopaySettings()
      setAutopaySettings(data)
    } catch {
      // non-blocking — the manual pay flow works without autopay info
      setAutopaySettings(null)
    }
  }

  const manageSelectedTransaction = (id) => {
    if (selectedTransactions.includes(id)) {
      selectTransaction(selectedTransactions.filter((transaction) => transaction !== id))
    } else {
      selectTransaction([...selectedTransactions, id])
    }
  }

  const onEditPrice = (data) => {
    if (isYardi) {
      if (data.chargeCode === 'total') {
        const totalFromTransactions = yardiTransactions.reduce((acc, charge) => {
          if (selectedTransactions.includes(charge.chargeCode)) {
            const transactionTotal =
              charge.fixedPrice !== undefined ? charge.fixedPrice : getChargePayable(charge)

            return acc + transactionTotal
          }
          return acc
        }, 0)

        const totalOriginal = totalFromTransactions.toString()
        if (!totalPay) {
          setTotalPay(totalOriginal)
        }
        setOriginalPay(totalOriginal)
        setEditPriceTransaction(data)
        setErrorMessage(null)
        return
      }

      const totalOriginal = getChargePayable(data).toString()
      const total = data.fixedPrice ? data.fixedPrice.toString() : totalOriginal

      setTotalPay(total)
      setOriginalPay(totalOriginal)
      setEditPriceTransaction(data)
      setErrorMessage(null)
    }
  }

  const saveNewValue = () => {
    if (isYardi && editPriceTransaction.chargeCode === 'total') {
      setCustomTotal(Number(totalPay))
      setTotalPay(Number(totalPay))
    } else if (isYardi) {
      setYardiTransactions(
        yardiTransactions.map((transObject) => {
          if (transObject.chargeCode === editPriceTransaction.chargeCode) {
            return { ...transObject, fixedPrice: Number(totalPay) }
          }
          return transObject
        }),
      )
    }
    setEditPriceTransaction(null)
  }

  const getOriginalValue = () => {
    setTotalPay(originalPay)
    setErrorMessage(null)
  }

  const isValid = (totalValue) => {
    const priceRegex = /^\d+(\.\d{0,2})?$/
    if (!originalPay) return true
    if (!priceRegex.test(totalValue)) {
      setErrorMessage(t('organisation:payments.errors.invalidPriceFormat'))
      return false
    } else if (Number(totalValue) > Number(originalPay)) {
      setErrorMessage(t('organisation:payments.errors.amountExceedsTotal'))
      return false
    } else {
      setErrorMessage('')
    }
    return true
  }

  const handleChangeValue = (e) => {
    setTotalPay(e.target.value)
    isValid(e.target.value)
  }

  useEffect(() => {
    if (yardiTransactions.length) {
      // When the resident can pick bills, start with none selected so they opt in to what they pay
      selectTransaction(
        paymentSettings.allowChargesSelection
          ? []
          : yardiTransactions
              .filter((transaction) => getChargePayable(transaction) > 0)
              .map((transaction) => transaction.chargeCode),
      )
    }
  }, [yardiTransactions.length, isPayNowMode, paymentSettings.allowChargesSelection])

  useEffect(() => {
    if (isYardi) {
      getClientYardiTransactions()
      getClientPaymentHistory()
      fetchPaymentSettings()
      fetchAutopaySettings()
    }
  }, [currentUser, isPayNowMode])

  if (isPayNowMode && currentUser) {
    return (
      <Elements stripe={stripePromise}>
        <PayNowView
          currentUser={currentUser}
          isYardi={isYardi}
          yardiTransactions={yardiTransactions}
          selectedTransactions={selectedTransactions}
          editTransaction={editPriceTransaction}
          totalPay={totalPay}
          errorMessage={errorMessage}
          paymentSettings={paymentSettings}
          onClose={() => {
            setPayNowMode(false)
            // a custom total is per visit — don't carry it into the next payment
            setCustomTotal(null)
            setTotalPay(null)
            setEditPriceTransaction(null)
          }}
          onEditPrice={onEditPrice}
          handleChangeValue={handleChangeValue}
          saveNewValue={saveNewValue}
          getOriginalValue={getOriginalValue}
          manageSelectedTransaction={manageSelectedTransaction}
          customTotal={customTotal}
          refetchTransactions={() => {
            getClientYardiTransactions()
            getClientPaymentHistory()
          }}
        />
      </Elements>
    )
  }

  if (isYardi) {
    return (
      <PayRentViewYardi
        isLoading={isLoading}
        transactions={yardiTransactions}
        paymentSettings={paymentSettings}
        setPayNowMode={() => setPayNowMode(true)}
        autopaySettings={autopaySettings}
        refetchAutopay={fetchAutopaySettings}
        paymentHistory={paymentHistory}
        paymentHistoryError={paymentHistoryError}
        isPmsHistoryUnavailable={isPmsHistoryUnavailable}
        blockPayment={blockPayment}
        blockPaymentMessage={blockPaymentMessage}
      />
    )
  }

  return null
}

Payments.propTypes = {
  currentUser: PropTypes.object,
}

export default connect((state) => {
  return {
    currentUser: selectUserData(state),
  }
})(Payments)
